import {
  type FieldPath,
  type FieldValues,
  useController,
  type UseControllerProps,
  type UseControllerReturn,
} from 'react-hook-form';

import { type LicenseFieldProps } from '../components/LicenseField/LicenseFieldController';
import { useLicenseContext } from '../LicenseModeContext';

import useLicenseConfig from './useLicenseConfig';
import { getInputProps } from '../helpers';

/**
 * @example
 * ```tsx
 * function Input(props) {
 *   const { field, fieldState, formState, config, computedProps } = useLicenseController(props);
 *   return (
 *     <div>
 *       <input {...field} placeholder={props.name} disabled={computedProps.disabled} />
 *       <p>{fieldState.isTouched && "Touched"}</p>
 *       <p>{formState.isSubmitted ? "submitted" : ""}</p>
 *     </div>
 *   );
 * }
 * ```
 */

export type UseLicenseControllerReturn<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> = UseControllerReturn<TFieldValues, TName> & {
  config: {
    isDisabled?: boolean;
    isHidden?: boolean;
  };
  computedProps: Record<string, any>;
};

export function useLicenseController<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  props: UseControllerProps<TFieldValues, TName> & LicenseFieldProps & { viewMode?: boolean; isCheckBox?: boolean }
): UseLicenseControllerReturn<TFieldValues, TName> {
  const { configName, featureFlag, viewMode: propsViewMode, isCheckBox, ...controllerProps } = props;
  const { viewMode } = useLicenseContext();

  const config = useLicenseConfig({ configName, featureFlag });
  const fieldProps = useController<TFieldValues, TName>(controllerProps);

  return {
    ...fieldProps,
    config,
    computedProps: {
      ...getInputProps(
        {
          isViewMode: propsViewMode ?? viewMode,
          isDisabled: config?.isDisabled || props.disabled,
          value: fieldProps.field.value,
          isSubmitting: fieldProps.formState.isSubmitting,
          error: fieldProps.fieldState.error,
        },
        isCheckBox
      ),
    },
  };
}
